import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './WorkspaceMobile.css';

import blueShieldImg from '../../assets/blueshield.png';
import workspaceVisibilityImg from '../../assets/home/workspace_visibility.png';
import aiGovernancePolicyImg from '../../assets/home/ai_governance_policy.png';
import isoChartImg from '../../assets/home/iso_chart.png';
import nistChartImg from '../../assets/home/nist_chart.png';
import soc2ChartImg from '../../assets/home/soc2_chart.png';
import openclawWarningImg from '../../assets/home/openclaw_warning.png';
import isoTableImg from '../../assets/iso_table.png';
import workspacePolicyImg from '../../assets/workspace_policy.png';

const WorkspaceMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const title = el.querySelector('h2, .wsm-cta-btn, p');
          const target = title || el;
          const nav = document.querySelector('.m-nav-container');
          const navH = nav ? nav.offsetHeight : 56;
          window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - navH - 40, behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const sectionIds = ['ws-hero', 'section-visibility', 'section-shadow-ai', 'section-governance', 'section-compliance', 'section-policy', 'section-ws-cta'];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      setCanScrollUp(scrollTop > 10);
      setCanScrollDown(scrollTop + winHeight < docHeight - 10);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getScrollTarget = (section) => {
    const title = section.querySelector('h2, .wsm-cta-btn, p');
    const target = title || section;
    const nav = document.querySelector('.m-nav-container');
    const navH = nav ? nav.offsetHeight : 56;
    return target.getBoundingClientRect().top + window.scrollY - navH - 40;
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: getScrollTarget(el), behavior: 'smooth' });
    }
  };

  const scrollToSection = useCallback((direction) => {
    const currentScroll = window.scrollY;
    const nav = document.querySelector('.m-nav-container');
    const navH = nav ? nav.offsetHeight : 56;
    const positions = sectionIds.map(id => {
      const el = document.getElementById(id);
      if (!el) return null;
      const title = el.querySelector('h2, .wsm-cta-btn, p');
      const target = title || el;
      return { top: target.getBoundingClientRect().top + window.scrollY, id, scrollTo: target.getBoundingClientRect().top + window.scrollY - navH - 40 };
    }).filter(Boolean);

    if (direction === 'down') {
      const next = positions.find(p => p.scrollTo > currentScroll + 10);
      if (next) window.scrollTo({ top: next.scrollTo, behavior: 'smooth' });
    } else {
      const prev = [...positions].reverse().find(p => p.scrollTo < currentScroll - 10);
      if (prev) window.scrollTo({ top: prev.scrollTo, behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <div className="scroll-float-wrap">
        {canScrollUp && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('up')} aria-label="Scroll up">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><polygon points="12,6 4,16 20,16" fill="currentColor"/></svg>
          </button>
        )}
        {canScrollDown && (
          <button className="scroll-float-bubble scroll-bubble-dark" onClick={() => scrollToSection('down')} aria-label="Scroll down">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><polygon points="12,18 4,8 20,8" fill="currentColor"/></svg>
          </button>
        )}
      </div>

      <section id="ws-hero" className="main-hero demo-hero wsm-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <img src={blueShieldImg} alt="Bltz Workspace" className="wsm-hero-shield" />
        <h1 className="hero-page-title">Bltz Workspace</h1>
      </section>

      <section className="wsm-content">
        <div className="wsm-layout">
          <h2 className="wsm-heading">AI Governance Automation</h2>
          <p className="wsm-description">
            AI governance automation platform that provides organizations with visibility, policy enforcement, automation, and compliance reporting for how employees and teams use AI tools.
          </p>

          <div className="wsm-cta" style={{ marginTop: 0, marginBottom: 16 }}>
            <button className="wsm-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              Get a Live Demo &rarr;
            </button>
          </div>

          {/* Visibility Section */}
          <div id="section-visibility" className="wsm-section wsm-section-light">
            <h2 className="wsm-section-title">See Every AI System</h2>
            <p className="wsm-section-desc">
              Gain complete visibility into every AI asset across your organization — agents, copilots, LLMs, and shadow AI — mapped and monitored in real time with a centralized governance dashboard.
            </p>
            <div className="wsm-img-wrap">
              <img src={workspaceVisibilityImg} alt="AI Asset Visibility" className="wsm-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Shadow AI Section */}
          <div id="section-shadow-ai" className="wsm-section">
            <h2 className="wsm-section-title">Block Unsanctioned AI Tools</h2>
            <p className="wsm-section-desc">
              Detect, control, and stop unauthorized AI applications before sensitive data is exposed. Monitor employee AI usage across your organization and enforce acceptable-use policies in real time.
            </p>
            <div className="wsm-img-wrap">
              <img src={openclawWarningImg} alt="Block Unsanctioned AI" className="wsm-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Governance Section */}
          <div id="section-governance" className="wsm-section wsm-section-light">
            <h2 className="wsm-section-title">Automate AI Governance</h2>
            <p className="wsm-section-desc">
              Enforce policies across prompts, data flows, and AI usage — automatically. Define rules for acceptable use, data handling, and model access that are applied consistently across your entire organization without manual intervention.
            </p>
            <div className="wsm-cta" style={{ marginTop: 0, marginBottom: 20 }}>
              <button className="wsm-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
                Get a Live Demo &rarr;
              </button>
            </div>
            <div className="wsm-img-wrap">
              <img src={aiGovernancePolicyImg} alt="AI Governance Policy" className="wsm-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* Compliance Section */}
          <div id="section-compliance" className="wsm-section">
            <h2 className="wsm-section-title">Built for Enterprise Compliance</h2>
            <p className="wsm-section-desc">
              Pre-built compliance frameworks aligned to NIST AI RMF, ISO 42001, and SOC 2 — out of the box. Continuous compliance monitoring, automated evidence collection, and audit-ready reporting for your AI operations.
            </p>
            <div className="wsm-compliance-grid">
              <div className="wsm-compliance-card">
                <img src={isoTableImg} alt="ISO Table" className="wsm-img" onContextMenu={e => e.preventDefault()} draggable={false} />
              </div>
              <div className="wsm-compliance-card">
                <img src={isoChartImg} alt="ISO 42001" className="wsm-img" onContextMenu={e => e.preventDefault()} draggable={false} />
              </div>
              <div className="wsm-compliance-card">
                <img src={nistChartImg} alt="NIST AI RMF" className="wsm-img" onContextMenu={e => e.preventDefault()} draggable={false} />
              </div>
              <div className="wsm-compliance-card">
                <img src={soc2ChartImg} alt="SOC 2" className="wsm-img" onContextMenu={e => e.preventDefault()} draggable={false} />
              </div>
            </div>
          </div>

          {/* Policy Automation Section */}
          <div id="section-policy" className="wsm-section wsm-section-light">
            <h2 className="wsm-section-title">AI Governance Policy Automation</h2>
            <p className="wsm-section-desc">
              Automate the creation, enforcement, and monitoring of AI governance policies across your organization. Define acceptable-use rules, data handling requirements, and model access controls that are enforced consistently — eliminating manual policy management.
            </p>
            <div className="wsm-img-wrap">
              <img src={workspacePolicyImg} alt="AI Governance Policy Automation" className="wsm-img" onContextMenu={e => e.preventDefault()} draggable={false} />
            </div>
          </div>

          {/* CTA */}
          <div id="section-ws-cta" className="wsm-cta">
            <button className="wsm-cta-btn" onClick={() => { navigate('/book-demo'); window.scrollTo(0, 0); }}>
              See AI Governance in Action &rarr;
            </button>
          </div>

          <span className="wsm-back-link" onClick={() => { navigate('/'); window.scrollTo(0, 0); }} style={{ cursor: 'pointer' }}>
            ← Back to Home
          </span>
        </div>
      </section>
    </>
  );
};

export default WorkspaceMobile;
